import React from 'react';
import moment from 'moment';
import { sortBy } from 'lodash';
// Api
import { GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity } from 'api/campaignV2/types/GetCampaignsV2';
// Helpers
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
import { getCategory } from 'helpers/customCampaign';
import {
  getContentDueDateLabel,
  getPostDueDateLabel,
} from 'helpers/timelineDeliverables';
import { getUgcContentDueDateLabel } from 'helpers/campaignUgc';
import { getMediaTypeText } from 'helpers/socialMediaContentTypes';
// Styles
import styles from '../CampaignActivityData.module.scss';

type CustomBriefActivityProps = {
  customBriefActivity: GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity;
};

const CustomBriefActivity = ({
  customBriefActivity,
}: CustomBriefActivityProps): JSX.Element | null => {
  if (!customBriefActivity) {
    return null;
  }

  const {
    brandName,
    website,
    instructions,
    encouraged,
    avoid,
    physicalProduct,
    productValue,
    products,
    promoCodes,
    promoUrls,
    contracts,
    tasksV2,
  } = customBriefActivity;

  return (
    <>
      {brandName && (
        <div>
          <p className={styles.label}>Brand Name</p>
          <p className={styles.value}>{brandName}</p>
        </div>
      )}

      {website && (
        <div>
          <p className={styles.label}>Website</p>
          <p className={styles.value}>{website}</p>
        </div>
      )}

      {!!instructions && (
        <div>
          <p className={styles.label}>Campaign brief</p>
          <p
            className={styles.value}
            dangerouslySetInnerHTML={{
              __html: parseForInnerHTMLWithBreaks(instructions),
            }}
          />
        </div>
      )}

      <div>
        <p className={styles.label}>Encouraged</p>
        <p
          className={styles.value}
          dangerouslySetInnerHTML={{
            __html: parseForInnerHTMLWithBreaks(encouraged ?? 'n/a'),
          }}
        />
      </div>

      <div>
        <p className={styles.label}>Please avoid</p>
        <p
          className={styles.value}
          dangerouslySetInnerHTML={{
            __html: parseForInnerHTMLWithBreaks(avoid ?? 'n/a'),
          }}
        />
      </div>

      <div>
        <p className={styles.label}>Physical product</p>
        <p className={styles.value}>{physicalProduct ? 'Yes' : 'No'}</p>
      </div>

      {!!productValue && (
        <div>
          <p className={styles.label}>Product value</p>
          <p className={styles.value}>${productValue}</p>
        </div>
      )}

      {!!products?.length && (
        <div>
          <p className={styles.label}>Products</p>
          {products.map((product, index) => (
            <div
              key={`${product.name}-${index}`}
              className={styles.deliverableContainer}
            >
              <p className={styles.label}>{product.name}</p>
              <div className={styles.deliverableDetails}>
                {product.description && (
                  <p className={styles.value}>
                    <strong>Description:</strong> {product.description}
                  </p>
                )}
                <p className={styles.value}>
                  <strong>Cost:</strong> ${product.cost}
                </p>
                <p className={styles.value}>
                  <strong>Quantity:</strong> {product.qty}
                </p>
                <p className={styles.value}>
                  <strong>Shipping:</strong> {product.shipping ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <p className={styles.label}>Promotion codes</p>
        {(promoCodes?.length ?? 0) > 0 ? (
          promoCodes?.map((item) => (
            <p key={item} className={styles.value}>
              {item}
            </p>
          ))
        ) : (
          <p className={styles.value}>n/a</p>
        )}
      </div>

      <div>
        <p className={styles.label}>Promotion urls</p>
        {(promoUrls?.length ?? 0) > 0 ? (
          promoUrls?.map((item) => (
            <p key={item} className={styles.value}>
              {item}
            </p>
          ))
        ) : (
          <p className={styles.value}>n/a</p>
        )}
      </div>

      {!!contracts?.length && (
        <div>
          <p className={styles.label}>Additional contracts</p>
          {contracts.map((contract) => (
            <p key={contract.key} className={styles.value}>
              <a
                href={contract.url || ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                {contract.url || contract.key}
              </a>
            </p>
          ))}
        </div>
      )}

      {!!tasksV2?.length && (
        <div>
          <p className={styles.label}>Deliverables</p>
          {sortBy(tasksV2, 'orderIdx').map((task, index) => {
            const category = getCategory(task);
            const isAppearance = category === 'Appearance';
            const isUgc = category === 'UGC';

            return (
              <div
                key={task.id || `${task.title}-${index}`}
                className={styles.deliverableContainer}
              >
                <p className={styles.label}>
                  {`Deliverable ${index + 1}:`}{' '}
                  {task.title || task.hrType || task.type}
                </p>

                <div className={styles.deliverableDetails}>
                  <p className={styles.value}>
                    <strong>Type:</strong> {task.hrType || task.type}
                  </p>

                  {task.hrPlatform && (
                    <p className={styles.value}>
                      <strong>Platform:</strong> {task.hrPlatform}
                    </p>
                  )}

                  {task.description && (
                    <p className={styles.value}>
                      <strong>Description:</strong> {task.description}
                    </p>
                  )}

                  {task.suggestedCaption && (
                    <p className={styles.value}>
                      <strong>Suggested caption:</strong>{' '}
                      {task.suggestedCaption}
                    </p>
                  )}

                  {!!task.hashtags?.length && (
                    <p className={styles.value}>
                      <strong>Hashtags:</strong> {task.hashtags.join(', ')}
                    </p>
                  )}

                  {!!task.mentions?.length && (
                    <p className={styles.value}>
                      <strong>Mentions:</strong> {task.mentions.join(', ')}
                    </p>
                  )}

                  {!isAppearance && (
                    <p className={styles.value}>
                      <strong>Content Due Date:</strong>{' '}
                      {isUgc
                        ? getUgcContentDueDateLabel(task)
                        : getContentDueDateLabel(task)}
                    </p>
                  )}

                  {!isAppearance && !isUgc && (
                    <p className={styles.value}>
                      <strong>Post Due Date:</strong>{' '}
                      {getPostDueDateLabel(task)}
                    </p>
                  )}

                  {task.__typename === 'CampaignAppearanceTask' && (
                    <>
                      {task.dueDate?.fixedDate && (
                        <p className={styles.value}>
                          <strong>Date:</strong>{' '}
                          {moment(task.dueDate.fixedDate).format(
                            'MM/DD/YYYY, hh:mm A'
                          )}
                        </p>
                      )}
                      {task.appearanceType && (
                        <p className={styles.value}>
                          <strong>Appearance type:</strong>{' '}
                          {task.appearanceType}
                        </p>
                      )}
                      {task.durationText && (
                        <p className={styles.value}>
                          <strong>Duration:</strong> {task.durationText}
                        </p>
                      )}
                      {task.location && (
                        <p className={styles.value}>
                          <strong>Location:</strong> {task.location}
                        </p>
                      )}
                      <p className={styles.value}>
                        <strong>Travel included:</strong>{' '}
                        {task.travelIncluded ? 'Yes' : 'No'}
                      </p>
                      <p className={styles.value}>
                        <strong>Accommodation included:</strong>{' '}
                        {task.accommodationIncluded ? 'Yes' : 'No'}
                      </p>
                      {task.maxExpenses != null && (
                        <p className={styles.value}>
                          <strong>Max expenses:</strong> ${task.maxExpenses}
                        </p>
                      )}
                    </>
                  )}

                  {!!task.contentItems?.length && (
                    <div className={styles.contentItemsContainer}>
                      <p className={styles.label}>Content Pieces</p>
                      {sortBy(task.contentItems, 'orderIdx').map(
                        (item, itemIndex) => (
                          <div
                            key={item.id || `${item.title}-${itemIndex}`}
                            className={styles.contentItem}
                          >
                            <p className={styles.value}>
                              <strong>Content {itemIndex + 1}:</strong>
                            </p>
                            {item.title && (
                              <p className={styles.value}>
                                <strong>Content Description:</strong>{' '}
                                {item.title}
                              </p>
                            )}
                            {item.description && (
                              <p className={styles.value}>
                                <strong>Instructions:</strong>{' '}
                                {item.description}
                              </p>
                            )}
                            {item.__typename === 'CampaignContentTask' &&
                              item.mediaType && (
                                <p className={styles.value}>
                                  <strong>Media Type:</strong>{' '}
                                  {getMediaTypeText(item.mediaType)}
                                </p>
                              )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default CustomBriefActivity;
